import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { komplyanceAiChatMessage } from '@app/pages';

// Within this many px of the bottom the thread is considered "at bottom" and we
// keep following new content. Generous enough to survive sub-pixel rounding and
// the layout shift of a streaming chunk landing.
const NEAR_BOTTOM_THRESHOLD = 120;
// Minimum upward movement (px) that counts as a deliberate user scroll-up.
// Anything smaller is treated as noise / momentum and does not break the pin.
const SCROLL_UP_DELTA = 4;

interface UseScrollToBottomProps {
  messages: komplyanceAiChatMessage[];
  endOfPageRef: React.RefObject<HTMLDivElement | null>;
}

interface UseScrollToBottomReturn {
  showScrollButton: boolean;
  hasNewMessage: boolean;
  isScrollingDown: boolean;
  messagesContainerRef: React.RefObject<HTMLDivElement | null>;
  handleScroll: () => void;
  /**
   * Scroll the thread to the latest message and re-engage auto-follow. Use
   * `behavior: "auto"` to snap immediately (e.g. right after the user sends);
   * default is smooth for the floating "scroll to bottom" control.
   */
  scrollToBottom: (options?: { behavior?: ScrollBehavior }) => void;
}

/**
 * Stick-to-bottom scroll behaviour, matching ChatGPT/Claude/Gemini:
 *
 * - While the user is at (or near) the bottom we are "pinned" and follow every
 *   new chunk/message, so a streaming response stays in view automatically.
 * - The moment the user deliberately scrolls up we unpin and leave them exactly
 *   where they are — they may be reading earlier content — and surface the
 *   scroll-to-bottom button instead of yanking them down when the stream ends.
 * - Scrolling back down to the bottom (or clicking the button) re-pins and
 *   resumes following.
 *
 * The pin lives in a ref, not state, so following never lags behind a render.
 */
export const useScrollToBottom = ({
  messages,
  endOfPageRef,
}: UseScrollToBottomProps): UseScrollToBottomReturn => {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  // Are we following the bottom of the thread? Starts true so the initial load
  // lands at the latest message.
  const isPinnedRef = useRef(true);
  const lastScrollTopRef = useRef(0);
  const prevAiCountRef = useRef(0);

  const [showScrollButton, setShowScrollButton] = useState(false);
  const [hasNewMessage, setHasNewMessage] = useState(false);

  const distanceFromBottom = useCallback((): number => {
    const el = messagesContainerRef.current;
    if (!el) return 0;
    return el.scrollHeight - el.scrollTop - el.clientHeight;
  }, []);

  const scrollToBottom = useCallback(
    (options?: { behavior?: ScrollBehavior }) => {
      const behavior = options?.behavior ?? 'smooth';
      endOfPageRef.current?.scrollIntoView({ behavior, block: 'end' });
      isPinnedRef.current = true;
      setShowScrollButton(false);
      setHasNewMessage(false);
    },
    [endOfPageRef],
  );

  const handleScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;

    const currentScrollTop = el.scrollTop;
    const scrolledUp = currentScrollTop < lastScrollTopRef.current - SCROLL_UP_DELTA;
    lastScrollTopRef.current = currentScrollTop;

    const atBottom = distanceFromBottom() <= NEAR_BOTTOM_THRESHOLD;

    if (scrolledUp && !atBottom) {
      // User took control — stop following until they come back down.
      isPinnedRef.current = false;
      setShowScrollButton(true);
    } else if (atBottom) {
      // Back at the bottom — resume following and clear the indicator.
      isPinnedRef.current = true;
      setShowScrollButton(false);
      setHasNewMessage(false);
    }
  }, [distanceFromBottom]);

  // Follow new content (new messages + every streaming chunk). `messages` is the
  // display list, whose identity changes on each streaming tick, so this runs as
  // the response grows. useLayoutEffect keeps the scroll in sync before paint so
  // following is jank-free.
  useLayoutEffect(() => {
    const aiCount = messages.filter((msg) => msg.type === 'ai').length;
    const prevAiCount = prevAiCountRef.current;
    prevAiCountRef.current = aiCount;

    // The list shrank — a different/cleared conversation was loaded. Re-pin so
    // the freshly loaded thread opens at its latest message.
    if (aiCount < prevAiCount) {
      isPinnedRef.current = true;
    }

    if (isPinnedRef.current) {
      endOfPageRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
      if (messagesContainerRef.current) {
        lastScrollTopRef.current = messagesContainerRef.current.scrollTop;
      }
      setShowScrollButton(false);
      setHasNewMessage(false);
      return;
    }

    // Not following and content changed below the fold — surface the button, and
    // label it "New Message" when a brand-new message (not just a streaming
    // chunk) arrived while the user was reading earlier content.
    setShowScrollButton(true);
    if (aiCount > prevAiCount) {
      setHasNewMessage(true);
    }
  }, [messages, endOfPageRef]);

  return {
    showScrollButton,
    hasNewMessage,
    // Retained for API compatibility with ScrollToBottomButton. The pin model
    // no longer distinguishes a transient "scrolling down" state.
    isScrollingDown: false,
    messagesContainerRef,
    handleScroll,
    scrollToBottom,
  };
};
